'use client';

import { CacheKey } from '@eliadmin/core/src/api/constants';
import { fetchProductReviews } from '@eliadmin/core/src/api/products/product-api';
import { useGlobalStore } from '@eliadmin/core/src/stores/global-store';
import { useLocaleStore } from '@eliadmin/core/src/stores/locale-store';
import { useVariantsStore } from '@eliadmin/core/src/stores/variant-store';
import { useQuery } from '@tanstack/react-query';

export type ProductRating = { average: number; count: number };

/**
 * Average rating and number of reviews. Same source and query key as the reviews tab: the review
 * list when it is loaded, the aggregate of the product until then. `null` when the product has no
 * valid rating or the store switched reviews off.
 */
export function useProductRating(): ProductRating | null {
  const product = useVariantsStore(state => state.product);
  const locale = useLocaleStore(state => state.currentLocale);
  const reviewsEnabled = useGlobalStore(
    state => state.templateSettings.product_detail?.reviews?.enabled,
  );

  const { data: reviews } = useQuery({
    queryKey: [CacheKey.PRODUCTS_REVIEWS, locale, product.slug],
    queryFn: () => fetchProductReviews({ locale, slug: product.slug }),
    enabled: !!reviewsEnabled,
  });

  if (!reviewsEnabled) return null;

  const count = reviews?.data.length ?? product.ratingCount ?? product.reviews_count ?? 0;
  const average = reviews?.data.length
    ? reviews.data.reduce((sum, review) => sum + Number(review.rating), 0) / reviews.data.length
    : Number(product.rating);

  if (count < 1 || !Number.isFinite(average) || average < 1 || average > 5) return null;
  return { average, count };
}
